import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import LessonPlansService from "../service/lessonPlans";
import HttpClient from "../network/http";
import { fetchCsrfToken, useAuthContext } from "../context/AuthContext";

const baseURL = process.env.REACT_APP_BASE_URL;

export default function useLessonPlans(conditions = {}) {
  const queryClient = useQueryClient();
  const { address } = useAuthContext();
  const httpClient = new HttpClient(baseURL, () => fetchCsrfToken(), address);
  const lessonPlansService = new LessonPlansService(httpClient);

  // 특정 수업의 수업계획서 조회 (mutation으로 변경)
  const lessonPlanQuery = useMutation(({ lessonId, ...queryConditions }) => {
    return lessonPlansService.findByLessonId(lessonId, {
      ...conditions,
      ...queryConditions,
    });
  });

  // 수업계획서 Upsert (생성 또는 수정)
  const upsertLessonPlan = useMutation((data) =>
    lessonPlansService.upsertLessonPlan(data)
  );

  // 수업계획서 생성
  const createLessonPlan = useMutation(
    (data) => lessonPlansService.createLessonPlan(data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["lessonPlan", variables.lesson_id]);
      },
    }
  );

  // 수업계획서 수정
  const updateLessonPlan = useMutation(
    ({ id, data }) => lessonPlansService.updateLessonPlan(id, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["lessonPlan", variables.data.lesson_id]);
      },
    }
  );

  // 수업계획서 삭제
  const deleteLessonPlan = useMutation(
    (id) => lessonPlansService.deleteLessonPlan(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["lessonPlan"]);
      },
    }
  );

  // 다른 수업의 계획서 조회 (동적 호출용)
  const fetchLessonPlanById = async (targetLessonId, targetConditions = {}) => {
    return lessonPlansService.findByLessonId(targetLessonId, targetConditions);
  };

  return {
    lessonPlanQuery,
    upsertLessonPlan,
    createLessonPlan,
    updateLessonPlan,
    deleteLessonPlan,
    fetchLessonPlanById,
  };
}

// 샘플 전용 훅
export function useLessonPlanSamples(conditions = {}) {
  const queryClient = useQueryClient();
  const { address } = useAuthContext();
  const httpClient = new HttpClient(baseURL, () => fetchCsrfToken(), address);
  const lessonPlansService = new LessonPlansService(httpClient);

  // 샘플 목록 조회
  const samplesQuery = useQuery(
    ["lessonPlanSamples", conditions],
    () => lessonPlansService.findSamples(conditions),
    {
      staleTime: 1000 * 60 * 5, // 5분
      retry: false,
    }
  );

  // 샘플 Upsert (학년도/학기당 1개만 유지)
  const createSample = useMutation(
    (data) => lessonPlansService.upsertSample(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["lessonPlanSamples"]);
      },
    }
  );

  return {
    samplesQuery,
    createSample,
  };
}
